import React from "react";
import {
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CardHeader,
  Box,
  Typography,
  Card,
  CardContent,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

// Metro/Province list: Metro Manila first, then alphabetically
const METRO_PROVINCES = [
  "Metro Manila",
  "Abra",
  "Agusan del Norte",
  "Agusan del Sur",
  "Aklan",
  "Albay",
  "Antique",
  "Apayao",
  "Aurora",
  "Bataan",
  "Batanes",
  "Batangas",
  "Basilan",
  "Benguet",
  "Biliran",
  "Bohol",
  "Bukidnon",
  "Bulacan",
  "Cagayan",
  "Camarines Norte",
  "Camarines Sur",
  "Capiz",
  "Catanduanes",
  "Cavite",
  "Cebu",
  "Cotabato",
  "Davao de Oro",
  "Davao del Norte",
  "Davao del Sur",
  "Davao Occidental",
  "Davao Oriental",
  "Dinagat Islands",
  "Eastern Samar",
  "Guimaras",
  "Ifugao",
  "Ilocos Norte",
  "Ilocos Sur",
  "Iloilo",
  "Isabela",
  "Kalinga",
  "La Union",
  "Laguna",
  "Lanao del Norte",
  "Lanao del Sur",
  "Leyte",
  "Maguindanao del Norte",
  "Maguindanao del Sur",
  "Marinduque",
  "Masbate",
  "Misamis Occidental",
  "Misamis Oriental",
  "Mountain Province",
  "Negros Occidental",
  "Negros Oriental",
  "Northern Samar",
  "Nueva Ecija",
  "Nueva Vizcaya",
  "Occidental Mindoro",
  "Oriental Mindoro",
  "Palawan",
  "Pampanga",
  "Pangasinan",
  "Quezon",
  "Quirino",
  "Romblon",
  "Samar",
  "Sarangani",
  "Siquijor",
  "Sorsogon",
  "Southern Leyte",
  "Sulu",
  "Surigao del Norte",
  "Surigao del Sur",
  "Tarlac",
  "Tawi-Tawi",
  "Zambales",
  "Zamboanga del Norte",
  "Zamboanga del Sur",
  "Zamboanga Sibugay",
];

const ClientAddress = ({
  clientAddresses,
  nationalAddresses,
  handleAddressChange,
  handleCityChange,
  handleBarangayChange,
  handleAddressCheckboxChange,
  handleAddAddress,
  handleRemoveAddress,
  loading,
  errors,
}) => (
  <>
    <Grid item xs={12}>
      <Grid container paddingBottom={2}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" width="100%" flexWrap="wrap">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm="auto">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase", fontSize: "1rem" }}
                    mr={2}
                  >
                    Job Site Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />} 
                    onClick={handleAddAddress}
                    size="small"
                    disabled={loading}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    Add Another Address
                  </Button>
                </Grid>
              </Grid>
            </Box>
          }
          sx={{ padding: "16px" }}
        />
      </Grid>
      <Grid container spacing={2}>
        {clientAddresses.map((addr, idx) => (
          <Grid item xs={12} key={idx}>
            <Card variant="outlined" sx={{ marginBottom: "24px" }}>
              <CardContent sx={{ padding: "24px" }}>
                <Grid container spacing={3}>
                  {/* House/Unit Street - Full width on mobile, half on larger screens */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="House/Unit Street"
                      value={addr.house_unit_street}
                      onChange={e => handleAddressChange(idx, 'house_unit_street', e.target.value)}
                      fullWidth
                      required
                      size="small"
                      disabled={loading}
                      error={!!errors[`addresses[${idx}].house_unit_street`]}
                      helperText={errors[`addresses[${idx}].house_unit_street`]}
                    />
                  </Grid>
                  {/* Metro/Province */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" error={!!errors[`addresses[${idx}].metro_province`]}>  
                      <InputLabel id={`metro-label-${idx}`}>Metro/Province</InputLabel>
                      <Select
                        labelId={`metro-label-${idx}`}
                        label="Metro/Province"
                        value={addr.metro_province || ''}
                        onChange={e => handleAddressChange(idx, 'metro_province', e.target.value)}
                        disabled={loading}
                        size="small"
                      >
                        <MenuItem value="" disabled>Select Metro/Province</MenuItem>
                        {METRO_PROVINCES.map(p => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </Select>
                      {!!errors[`addresses[${idx}].metro_province`] && (
                        <FormHelperText>{errors[`addresses[${idx}].metro_province`]}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {/* City */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      options={[...new Set(
                        nationalAddresses
                          .filter(d => d.metro_province === addr.metro_province)
                          .map(d => d.city)
                      )].sort()}
                      value={addr.city || ''}
                      onChange={(e, v) => handleCityChange(idx, v)}
                      onInputChange={(e, v) => handleCityChange(idx, v)}
                      disabled={loading}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="City/Municipality"
                          size="small"
                          error={!!errors[`addresses[${idx}].city`]}
                          helperText={errors[`addresses[${idx}].city`]}
                        />
                      )}
                    />
                  </Grid>
                  {/* Barangay */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      options={[...new Set(
                        nationalAddresses
                          .filter(d => d.city === addr.city)
                          .map(d => d.barangay)
                      )].sort()}
                      value={addr.barangay || ''}
                      onChange={(e, v) => handleBarangayChange(idx, v)}
                      onInputChange={(e, v) => handleBarangayChange(idx, v)}
                      disabled={loading}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Barangay"
                          size="small"
                          error={!!errors[`addresses[${idx}].barangay`]}
                          helperText={errors[`addresses[${idx}].barangay`]}
                        />
                      )}
                    />
                  </Grid>
                  {/* Zip Code */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Zip Code"
                      value={addr.zipcode}
                      onChange={e => handleAddressChange(idx, 'zipcode', e.target.value)}
                      fullWidth
                      size="small"
                      disabled={loading}
                      error={!!errors[`addresses[${idx}].zipcode`]}
                      helperText={errors[`addresses[${idx}].zipcode`]}
                    />
                  </Grid>
                  {/* Area (sqm) */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Area (sqm)"
                      value={addr.area}
                      onChange={e => handleAddressChange(idx, 'area', e.target.value)}
                      fullWidth
                      size="small"
                      disabled={loading}
                      error={!!errors[`addresses[${idx}].area`]}
                      helperText={errors[`addresses[${idx}].area`]}
                    />
                  </Grid>
                  {/* Billing Address */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={<Checkbox checked={addr.billing} onChange={() => handleAddressCheckboxChange(idx)} size="small" disabled={loading} />} 
                      label="Billing Address"
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        height: '40px',
                        marginTop: 0,
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem'
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px'
                        }
                      }}
                    />
                  </Grid>
                  {/* Remove Address */}
                  {clientAddresses.length > 1 && (
                    <Grid item xs={12} textAlign="center">
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleRemoveAddress(idx)}
                        disabled={loading}
                      >
                        Remove Address
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Grid>
  </>
);

export default ClientAddress;
